/**
 * Crawl policy: which discovered detail URLs a run fetches.
 *
 * A job stores it at scrape_jobs.config.crawlPolicy; job-orchestrator resolves the effective
 * policy for each run (job config + a per-run override from the run-job request) and writes it
 * to job_runs.metadata.crawlPolicy. The scrape_queue_skip_known_url trigger reads it there, so
 * the policy is enforced after discovery and before the paid detail fetch.
 *
 * - skipComplete: skip a URL whose sale we already hold completely (default on). Complete is
 *   SQL sale_completeness: priced (any source), a non-SQM stored page, no likely_fake verdict,
 *   and a photo only when app setting crawl_policy.require_media is on.
 * - completeMissing: with skipComplete on, fetch a URL whose sale we hold incompletely, unless the
 *   same adapter fetched it in the last 30 days (default on). Off: skip every held sale.
 * - forceRecrawl: fetch every discovered URL again, for lost, damaged or poisoned data
 *   (default off; the "Run now (force recrawl)" action sets it for one run).
 *
 * Property-profile adapters (adapters.detail_url_identity = 'property') always fetch a known URL.
 * A URL already live in another run is skipped under every policy.
 */

export interface CrawlPolicy {
  skipComplete: boolean;
  completeMissing: boolean;
  forceRecrawl: boolean;
}

export const DEFAULT_CRAWL_POLICY: Readonly<CrawlPolicy> = Object.freeze({
  skipComplete: true,
  completeMissing: true,
  forceRecrawl: false,
});

export interface CrawlPolicyOverride {
  forceRecrawl?: unknown;
}

function readBoolean(source: unknown, key: keyof CrawlPolicy): boolean | undefined {
  if (!source || typeof source !== "object") return undefined;
  const value = (source as Record<string, unknown>)[key];
  return typeof value === "boolean" ? value : undefined;
}

/** Effective policy for one run: job config's crawlPolicy, then the run's override. */
export function resolveCrawlPolicy(
  jobConfig: unknown,
  override?: CrawlPolicyOverride | null,
): CrawlPolicy {
  const stored =
    jobConfig && typeof jobConfig === "object"
      ? (jobConfig as Record<string, unknown>).crawlPolicy
      : undefined;
  return {
    skipComplete: readBoolean(stored, "skipComplete") ?? DEFAULT_CRAWL_POLICY.skipComplete,
    completeMissing: readBoolean(stored, "completeMissing") ?? DEFAULT_CRAWL_POLICY.completeMissing,
    forceRecrawl:
      readBoolean(override, "forceRecrawl") ??
      readBoolean(stored, "forceRecrawl") ??
      DEFAULT_CRAWL_POLICY.forceRecrawl,
  };
}

/** True when the run fetches URLs whose sale we already hold completely. */
export function refetchesKnownSales(policy: CrawlPolicy): boolean {
  return policy.forceRecrawl || !policy.skipComplete;
}

/**
 * scrape_queue.last_error prefixes written by the scrape_queue_skip_known_url trigger on a
 * 'skipped' row. "dedupe: known-sale" (2026-10-13, phase 1: price + date held) and
 * "dedupe: complete sold snapshot" (before) are older texts for an already-held sale.
 * Order matters: the first matching reason wins.
 */
export const DEDUPE_SKIP_PREFIXES = {
  complete: ["dedupe: complete", "dedupe: known-sale"],
  "held-incomplete": ["dedupe: held-incomplete"],
  "incomplete-cooldown": ["dedupe: incomplete-cooldown"],
  // For-sale (buy) lane, follow-until-sold phase 1: this adapter fetched the URL < 30 days ago.
  "listing-cooldown": ["dedupe: listing-cooldown"],
  live: ["dedupe: live", "dedupe: url already queued"],
} as const;

export type DedupeSkipReason = keyof typeof DEDUPE_SKIP_PREFIXES;

export const DEDUPE_SKIP_REASONS = Object.keys(DEDUPE_SKIP_PREFIXES) as DedupeSkipReason[];

export function classifyDedupeSkip(lastError: string | null | undefined): DedupeSkipReason | null {
  if (!lastError) return null;
  for (const reason of DEDUPE_SKIP_REASONS) {
    if (DEDUPE_SKIP_PREFIXES[reason].some((prefix) => lastError.startsWith(prefix))) {
      return reason;
    }
  }
  return null;
}

/** Operator wording for each skip reason, as shown on the run-detail header and job History. */
export const DEDUPE_SKIP_LABELS: Record<DedupeSkipReason, string> = {
  complete: "already complete",
  "held-incomplete": "held incomplete",
  "incomplete-cooldown": "incomplete, fetched < 30 days ago",
  "listing-cooldown": "for sale, fetched < 30 days ago",
  live: "live in another run",
};

/** "12 already complete, 3 live in another run": the non-zero reasons, in display order. */
export function describeDedupeSkips(skipped: Record<DedupeSkipReason, number>): string {
  return DEDUPE_SKIP_REASONS.filter((reason) => skipped[reason] > 0)
    .map((reason) => `${skipped[reason].toLocaleString()} ${DEDUPE_SKIP_LABELS[reason]}`)
    .join(", ");
}
